import { useCallback, useEffect, useRef, useState } from "react";

export type LocalAutosaveStatus = "idle" | "dirty" | "savedLocally";

type LocalSnapshot = {
  title: string;
  description: string;
  sphere: number | null;
  variant: number | null;
};

type StoredDraft = LocalSnapshot & { savedAt: string };

const DEBOUNCE_MS = 1500;

const readLocalDraft = (storageKey: string): StoredDraft | null => {
  try {
    const raw = localStorage.getItem(storageKey);

    return raw ? (JSON.parse(raw) as StoredDraft) : null;
  } catch {
    return null;
  }
};

const removeLocalDraft = (storageKey: string): void => {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // недоступно — считаем, что черновика нет
  }
};

type UseAutosaveLocalDraftParams = {
  enabled: boolean;
  storageKey: string;
  titleInput: string;
  editedContent: string;
  selectedSphere: number | null;
  selectedVariant: number | null;
};

// Подстраховка для правок опубликованного контента: ничего не уходит на
// backend (иначе недописанное стало бы видно всем раньше сохранения) —
// только localStorage для этого элемента. Просмотр рендерится из props
// (БД), не из хука — черновик виден только в редакторе после "Восстановить".
export const useAutosaveLocalDraft = ({
  enabled,
  storageKey,
  titleInput,
  editedContent,
  selectedSphere,
  selectedVariant,
}: UseAutosaveLocalDraftParams) => {
  const [status, setStatus] = useState<LocalAutosaveStatus>("idle");
  const [pendingRestore, setPendingRestore] = useState<StoredDraft | null>(
    null,
  );
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const checkedRestoreRef = useRef(false);
  const lastWrittenRef = useRef<LocalSnapshot>({
    title: titleInput,
    description: editedContent,
    sphere: selectedSphere,
    variant: selectedVariant,
  });
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!enabled || checkedRestoreRef.current) return;
    checkedRestoreRef.current = true;

    const existing = readLocalDraft(storageKey);

    setPendingRestore(existing);
    if (existing) setLastSavedAt(new Date(existing.savedAt));
  }, [enabled, storageKey]);

  const flushNow = useCallback(() => {
    clearTimeout(timerRef.current);
    if (!enabled) return;

    const current: LocalSnapshot = {
      title: titleInput,
      description: editedContent,
      sphere: selectedSphere,
      variant: selectedVariant,
    };

    const unchanged =
      current.title === lastWrittenRef.current.title &&
      current.description === lastWrittenRef.current.description &&
      current.sphere === lastWrittenRef.current.sphere &&
      current.variant === lastWrittenRef.current.variant;

    if (unchanged) return;

    try {
      const now = new Date();
      const stored: StoredDraft = { ...current, savedAt: now.toISOString() };
      localStorage.setItem(storageKey, JSON.stringify(stored));
      lastWrittenRef.current = current;
      setStatus("savedLocally");
      setLastSavedAt(now);
    } catch {
      // недоступно — лишь подстраховка от потери текста
    }
  }, [
    enabled,
    storageKey,
    titleInput,
    editedContent,
    selectedSphere,
    selectedVariant,
  ]);

  useEffect(() => {
    if (!enabled) return undefined;

    const current: LocalSnapshot = {
      title: titleInput,
      description: editedContent,
      sphere: selectedSphere,
      variant: selectedVariant,
    };
    const isUnchangedFromLoaded =
      current.title === lastWrittenRef.current.title &&
      current.description === lastWrittenRef.current.description &&
      current.sphere === lastWrittenRef.current.sphere &&
      current.variant === lastWrittenRef.current.variant;

    if (isUnchangedFromLoaded) return undefined;

    setStatus("dirty");
    // Пользователь печатает без "Восстановить" — старый черновик всё равно
    // перезапишет flushNow, предлагать восстановить его больше не актуально.
    setPendingRestore(null);

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flushNow, DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
  }, [
    enabled,
    titleInput,
    editedContent,
    selectedSphere,
    selectedVariant,
    flushNow,
  ]);

  // ModalWindow размонтирует редактор при закрытии, а не скрывает —
  // незавершённый дебаунс иначе пропал бы молча. Коммитим через ref, чтобы
  // не попасть на устаревшее замыкание.
  const flushNowRef = useRef(flushNow);
  flushNowRef.current = flushNow;

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      flushNowRef.current();
    },
    [],
  );

  // Закрытие вкладки не размонтирует React — нужен отдельный шанс сохранить
  // перед уходом со страницы.
  useEffect(() => {
    const handler = () => flushNowRef.current();

    window.addEventListener("pagehide", handler);
    window.addEventListener("beforeunload", handler);

    return () => {
      window.removeEventListener("pagehide", handler);
      window.removeEventListener("beforeunload", handler);
    };
  }, []);

  const discardRestore = useCallback(() => {
    removeLocalDraft(storageKey);
    setPendingRestore(null);
    setLastSavedAt(null);
  }, [storageKey]);

  // Явное сохранение уже применило правку к записи — локальный черновик
  // больше не нужен.
  const clearAfterExplicitSave = useCallback(() => {
    removeLocalDraft(storageKey);
    lastWrittenRef.current = {
      title: titleInput,
      description: editedContent,
      sphere: selectedSphere,
      variant: selectedVariant,
    };
    setPendingRestore(null);
    setStatus("idle");
    setLastSavedAt(new Date());
  }, [storageKey]);

  // Подтверждённое закрытие без сохранения — убираем черновик и фиксируем
  // текущее содержимое "базовым", чтобы unmount-flushNow не перезаписал его.
  const discardCurrentDraft = useCallback(() => {
    removeLocalDraft(storageKey);
    lastWrittenRef.current = {
      title: titleInput,
      description: editedContent,
      sphere: selectedSphere,
      variant: selectedVariant,
    };
    setPendingRestore(null);
    setLastSavedAt(null);
    setStatus("idle");
  }, [storageKey, titleInput, editedContent, selectedSphere, selectedVariant]);

  const hasUnsavedChanges = status === "dirty" || status === "savedLocally";

  return {
    status,
    pendingRestore,
    lastSavedAt,
    hasUnsavedChanges,
    discardRestore,
    clearAfterExplicitSave,
    discardCurrentDraft,
    flushNow,
  };
};
